<script>
import {defineComponent, onBeforeMount, reactive, ref, watch, computed} from "vue";
import Editor from "primevue/editor";
import MainCard from "@/components/cards/MainCard";
import ButtonSuccess from "@/components/buttons/ButtonSuccess.vue";
import Button from "primevue/button";
import Skeleton from "primevue/skeleton"
import InputText from "primevue/inputtext";
import {corpClientApiCall, updateCorpClientApiCall} from "@/services/website";
import { useApiCall } from '@/composables/useApiCall'
import {useI18n} from "vue-i18n";
import {required} from "@/utils/i18n-validators";
import {useVuelidate} from "@vuelidate/core";
import {useStore} from "vuex";
export default defineComponent({
  components: {
    ButtonSuccess,
    Editor,
    MainCard,
    Button,
    Skeleton,
    InputText,
  },
  setup() {
    const {t} = useI18n()
    const store = useStore();

    const lang = computed(() => store.getters.getLang);
    const titleField = computed(() => `title_${lang.value}`);
    const descriptionField = computed(() => `description_${lang.value}`);

    const form = reactive({
      title_ru: '',
      title_en: '',
      description_ru: '',
      description_en: ''
    });
    const formRules = computed(() => ({
      [titleField.value]: {required},
      [descriptionField.value]: {required},
    }))
    const v$ = useVuelidate(formRules, form);
    const isUpdated = ref(false)
    const {
      isLoading: isContentLoading,
      data: contentData,
      error: contentError,
      executeApiCall: getContentAction,
    } = useApiCall(corpClientApiCall, true)

    const {
      isLoading: isContentUpdateLoading,
      data: contentUpdateData,
      error: contentUpdateError,
      executeApiCall: updateContentAction,
    } = useApiCall(updateCorpClientApiCall, true)

    const updateForm = async () => {
      const result = await v$.value.$validate();
      if (!result) {
        return;
      }
      try {
        await updateContentAction(form)
        if (contentUpdateData.value) {
          isUpdated.value = true
        }
      } catch (e) {
        console.error(e)
      }
    }

    watch(() => form,
        () => {
          isUpdated.value = false
        }, {deep: true})

    onBeforeMount( async () => {
      try {
        await getContentAction()
        if (contentData.value?.data) {
          form.title_ru = contentData.value.data.title_ru
          form.title_en = contentData.value.data.title_en || ''
          form.description_ru = contentData.value.data.description_ru
          form.description_en = contentData.value.data.description_en || ''
        }
      } catch (e) {
        console.error(e)
      }
    })

    return {
      t,
      v$,
      form,
      isUpdated,
      updateForm,
      isContentLoading,
      contentError,
      contentUpdateError,
      isContentUpdateLoading,
      titleField,
      descriptionField
    };
  }
})
</script>

<template>
  <div class="mt-7">
    <div class="flex justify-content-between align-items-center mb-4">
      <h1>{{ t('website.corp-clients.title')}}</h1>
      <ButtonSuccess v-if="isUpdated" :label="t('button.changesSaved')" class="btn-fix-height"/>
      <Button v-else @click="updateForm" :loading="isContentUpdateLoading" :label="t('button.saveChange')" class="btn-primary font-light btn-fix-height" />
    </div>
    <Skeleton v-if="isContentLoading" width="100%" height="100px" class="mb-3"></Skeleton>
    <MainCard v-else :title="t('title')" class="mb-3">
      <div class="grid mb-2 mt-2">
        <div class="w-full">
            <span class="p-float-label w-full">
              <InputText
                  v-model="form[titleField]"
                  :class="['w-full', {'p-invalid': v$[titleField].$errors?.length || contentUpdateError?.data[titleField]}]"
                  id="corp-title"
              />
              <label for="corp-title">{{ t('input.placeholder.title') }} <i style="color: #E26B6B">*</i></label>
            </span>
          <span v-if="v$[titleField].$errors?.length || contentUpdateError?.data[titleField]" class="text-xs color-error">
              {{ contentUpdateError ? contentUpdateError.data[titleField][0] : t(v$[titleField].$errors?.[0].$message) }}
          </span>
        </div>
      </div>
    </MainCard>
    <Skeleton v-if="isContentLoading" width="100%" height="100px"></Skeleton>
    <MainCard v-else :title="t('description')" class="mb-3">
      <Editor v-model="form[descriptionField]" class="w-full"></Editor>
      <span v-if="v$[descriptionField].$errors?.length || contentUpdateError?.data[descriptionField]" class="text-xs color-error">
              {{ contentUpdateError ? contentUpdateError.data[descriptionField][0] : t(v$[descriptionField].$errors?.[0].$message) }}
      </span>
    </MainCard>
  </div>
</template>

